import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import EmptyAddCardCst from "@components/EmptyAddCardCst";
import ItemCardCst from "@components/ItemCardCst";
import LanguageSelectorLinks from "@components/InputCst/LanguageSelector/Links";
import { WizardStepErrors } from "@components/WizardCst/types";
import { UsefulLink } from "../../../types";
import { useLoTranslationContext } from "../../../../hooks/useLoTranslation";
import UsefulLinkModalCst from "./UsefulLinkModalCst";

interface UsefulLinksFieldProps {
  values: UsefulLink[];
  onChange: (next: UsefulLink[]) => void;

  errors?: WizardStepErrors;
}

const UsefulLinksField: React.FC<UsefulLinksFieldProps> = ({
  values,
  onChange,
}) => {
  const { t } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const trans = useLoTranslationContext();
  const canTranslate = !!trans?.languages?.secondary_languages?.length;

  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleAdd = (link: UsefulLink) => {
    onChange([...values, link]);
  };

  const handleRemove = (idx: number) => {
    const next = values.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        {t("fields.useful-links")}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {values.map((link, idx) => (
          <ItemCardCst
            key={idx}
            title={link.url_label}
            segments={[link.url, link.description]}
            actions={
              canTranslate &&
              link.id != null &&
              trans && (
                <LanguageSelectorLinks
                  languages={trans.languages}
                  col={{ accessorKey: "useful_links" }}
                  id={corporateId}
                  idObject={link.id}
                />
              )
            }
            onDelete={() => handleRemove(idx)}
          />
        ))}
        <EmptyAddCardCst
          addLabel={t("common.add") || "Aggiungi"}
          onAdd={handleOpenModal}
          minHeight={values.length > 0 ? "64px" : "120px"}
        />
      </Box>

      <UsefulLinkModalCst
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleAdd}
      />
    </Box>
  );
};

export default UsefulLinksField;
